/**
 * Report Metrics
 *
 * Pure computation for the standard summary report. Every number the report
 * shows (and, later, every number Kai is allowed to cite) comes from one call
 * to computeSummaryReport with one cut date, so no two figures can disagree.
 *
 * Task visibility and the overdue rule match the dashboard (src/views/dashboard.js):
 * archived tasks and recurrence templates are hidden; a task is overdue when it
 * is open (todo / progress / review) and its end date is before the cut date.
 * Backlog tasks are never overdue.
 *
 * @module services/reportMetrics
 */

// Kanban order, left to right. Every legend, stacked bar and chip list follows it.
export const REPORT_STATUSES = ['backlog', 'todo', 'progress', 'review', 'done'];
// Priority chips in reports follow the same urgency order as the task UI.
export const REPORT_PRIORITIES = ['high', 'medium', 'low'];

const OPEN_STATUSES = new Set(['todo', 'progress', 'review']);
const DAY_MS = 86400000;
// A project counts as "behind" or "ahead" only when its completion is more than
// this many percentage points away from the share of its time already spent.
export const PACE_TOLERANCE = 10;
export const AGING_BUCKETS = [
    { key: '1-7', min: 1, max: 7 },
    { key: '8-14', min: 8, max: 14 },
    { key: '15-30', min: 15, max: 30 },
    { key: '31+', min: 31, max: Infinity }
];
// Severity order for project rows: what needs attention first.
const PROJECT_STATE_ORDER = ['late', 'behind', 'onTrack', 'ahead', 'notStarted', 'noDates', 'noTasks', 'done'];

function isoDay(value) {
    return (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) ? value.slice(0, 10) : '';
}

function dayMs(iso) {
    return Date.parse(`${iso}T00:00:00Z`);
}

/** Whole days from a to b (b - a), both YYYY-MM-DD. */
export function daysBetween(a, b) {
    return Math.round((dayMs(b) - dayMs(a)) / DAY_MS);
}

function addDays(iso, n) {
    return new Date(dayMs(iso) + n * DAY_MS).toISOString().slice(0, 10);
}

function monthKey(iso) {
    return iso.slice(0, 7);
}

function shiftMonth(key, delta) {
    const [y, m] = key.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    return d.toISOString().slice(0, 7);
}

function pct(part, whole) {
    return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

function isHiddenTask(task) {
    return !task || !!task.archivedAt || !!task.isTemplate;
}

function emptyIdLists() {
    return Object.fromEntries(REPORT_STATUSES.map(s => [s, []]));
}

function emptyCounts() {
    return Object.fromEntries(REPORT_STATUSES.map(s => [s, 0]));
}

function normalizeStatus(status) {
    return REPORT_STATUSES.includes(status) ? status : 'todo';
}

/** True when some task is in progress, in review or done (not only to-do/backlog). */
export function hasStartedWork(counts) {
    return (counts.progress || 0) + (counts.review || 0) + (counts.done || 0) > 0;
}

function isOverdue(task, today) {
    const end = isoDay(task.endDate);
    return OPEN_STATUSES.has(normalizeStatus(task.status)) && !!end && end < today;
}

function projectState(row, today) {
    if (row.total === 0) return 'noTasks';
    if (row.counts.done === row.total) return 'done';
    if (row.daysOverdue > 0) return 'late';
    if (row.startDate && row.startDate > today) return 'notStarted';
    if (row.timePct === null) return 'noDates';
    if (row.donePct < row.timePct - PACE_TOLERANCE) return 'behind';
    if (row.donePct > row.timePct + PACE_TOLERANCE) return 'ahead';
    return 'onTrack';
}

function buildProjectRow(project, projectTasks, today) {
    const counts = emptyCounts();
    const taskIdsByStatus = emptyIdLists();
    projectTasks.forEach(task => {
        const s = normalizeStatus(task.status);
        counts[s]++;
        taskIdsByStatus[s].push(task.id);
    });
    const total = projectTasks.length;
    const startDate = isoDay(project.startDate);
    const endDate = isoDay(project.endDate);
    const remaining = total - counts.done;

    let timePct = null;
    if (startDate && endDate && endDate > startDate) {
        const elapsed = daysBetween(startDate, today);
        const duration = daysBetween(startDate, endDate);
        timePct = Math.max(0, Math.min(100, Math.round((elapsed / duration) * 100)));
    }
    const daysOverdue = endDate && endDate < today && remaining > 0 ? daysBetween(endDate, today) : 0;

    const row = {
        id: project.id,
        name: String(project.name || ''),
        counts,
        taskIdsByStatus,
        total,
        remaining,
        donePct: pct(counts.done, total),
        startDate,
        endDate,
        timePct,
        daysOverdue,
        overdueTasks: projectTasks.filter(task => isOverdue(task, today)).length,
        // "In course": at least one task moved past to-do. Projects whose tasks are all
        // to-do/backlog (or that have none) are still being planned.
        started: hasStartedWork(counts)
    };
    row.state = projectState(row, today);
    return row;
}

function groupKey(task) {
    return `${String(task.title || '').trim().toLowerCase()}\u0000${task.projectId ?? ''}`;
}

/**
 * Compute every figure of the standard summary report.
 *
 * @param {Array} tasks - All tasks (hidden ones are filtered here)
 * @param {Array} projects - All projects (archived ones are filtered here)
 * @param {Object} options
 * @param {string} options.today - Cut date as YYYY-MM-DD in the user's time zone (required)
 * @param {number} [options.months=6] - Calendar months of throughput history, current month included
 * @param {number} [options.upcomingDays=30] - Window for the upcoming list
 * @param {number} [options.attentionLimit=15] - Max rows in the attention list (most delayed first)
 * @param {number} [options.upcomingLimit=10] - Max rows in the upcoming list
 * @returns {Object} Report facts (plain JSON, safe to persist or send to Kai)
 */
export function computeSummaryReport(tasks, projects, options = {}) {
    const today = isoDay(options.today);
    if (!today) throw new Error('computeSummaryReport: options.today (YYYY-MM-DD) is required');
    const monthsBack = Number.isFinite(options.months) ? Math.max(1, options.months) : 6;
    const upcomingDays = Number.isFinite(options.upcomingDays) ? options.upcomingDays : 30;
    const attentionLimit = Number.isFinite(options.attentionLimit) ? options.attentionLimit : 15;
    const upcomingLimit = Number.isFinite(options.upcomingLimit) ? options.upcomingLimit : 10;

    const liveProjects = (Array.isArray(projects) ? projects : []).filter(p => p && !p.archivedAt);
    const projectById = new Map(liveProjects.map(p => [Number(p.id), p]));
    const archivedProjectIds = new Set(
        (Array.isArray(projects) ? projects : []).filter(p => p && p.archivedAt).map(p => Number(p.id))
    );
    const visibleTasks = (Array.isArray(tasks) ? tasks : []).filter(task => !isHiddenTask(task));

    // ── KPIs ────────────────────────────────────────────────────────────
    const counts = emptyCounts();
    visibleTasks.forEach(task => { counts[normalizeStatus(task.status)]++; });
    const total = visibleTasks.length;
    const open = counts.todo + counts.progress + counts.review;
    const overdueTasks = visibleTasks.filter(task => isOverdue(task, today));
    const monthStart = `${monthKey(today)}-01`;
    const projectName = (id) => {
        const project = projectById.get(Number(id));
        return project ? String(project.name || '') : null;
    };

    // ── Projects ────────────────────────────────────────────────────────
    const tasksByProject = new Map();
    const unassigned = [];
    const inArchivedProjects = [];
    visibleTasks.forEach(task => {
        const pid = task.projectId == null || task.projectId === '' ? null : Number(task.projectId);
        if (pid !== null && projectById.has(pid)) {
            if (!tasksByProject.has(pid)) tasksByProject.set(pid, []);
            tasksByProject.get(pid).push(task);
        } else if (pid !== null && archivedProjectIds.has(pid)) {
            inArchivedProjects.push(task);
        } else {
            unassigned.push(task);
        }
    });

    const projectRows = liveProjects
        .map(project => buildProjectRow(project, tasksByProject.get(Number(project.id)) || [], today))
        .sort((a, b) => {
            const s = PROJECT_STATE_ORDER.indexOf(a.state) - PROJECT_STATE_ORDER.indexOf(b.state);
            if (s !== 0) return s;
            if (b.daysOverdue !== a.daysOverdue) return b.daysOverdue - a.daysOverdue;
            return a.name.localeCompare(b.name);
        });

    const countRow = (list) => {
        const c = emptyCounts();
        const ids = emptyIdLists();
        list.forEach(task => {
            const s = normalizeStatus(task.status);
            c[s]++;
            ids[s].push(task.id);
        });
        return { counts: c, taskIdsByStatus: ids, total: list.length };
    };

    const projectsInProgress = new Set(
        visibleTasks
            .filter(t => normalizeStatus(t.status) === 'progress' && projectById.has(Number(t.projectId)))
            .map(t => Number(t.projectId))
    ).size;

    const nextMilestone = liveProjects
        .map(p => ({ id: p.id, name: String(p.name || ''), endDate: isoDay(p.endDate) }))
        .filter(p => p.endDate && p.endDate >= today)
        .sort((a, b) => a.endDate.localeCompare(b.endDate) || a.name.localeCompare(b.name))[0] || null;
    if (nextMilestone) nextMilestone.daysLeft = daysBetween(today, nextMilestone.endDate);

    // ── Throughput (calendar months, current month partial) ─────────────
    const currentMonth = monthKey(today);
    const monthKeys = [];
    for (let i = monthsBack - 1; i >= 0; i--) monthKeys.push(shiftMonth(currentMonth, -i));
    const firstMonth = monthKeys[0];
    const createdDay = task => isoDay(task.createdAt) || isoDay(task.createdDate);
    const completedDay = task => normalizeStatus(task.status) === 'done' ? isoDay(task.completedDate) : '';

    const months = monthKeys.map(key => ({ month: key, created: 0, completed: 0, cumCreated: 0, cumCompleted: 0 }));
    const monthIndex = new Map(monthKeys.map((k, i) => [k, i]));
    let createdBefore = 0;
    let completedBefore = 0;
    let doneWithoutCompletedDate = 0;
    visibleTasks.forEach(task => {
        const c = createdDay(task);
        // Tasks with no creation date predate the window.
        if (!c || monthKey(c) < firstMonth) createdBefore++;
        else if (monthIndex.has(monthKey(c)) && c <= today) months[monthIndex.get(monthKey(c))].created++;

        if (normalizeStatus(task.status) !== 'done') return;
        const d = completedDay(task);
        if (!d) { doneWithoutCompletedDate++; return; }
        if (monthKey(d) < firstMonth) completedBefore++;
        else if (monthIndex.has(monthKey(d)) && d <= today) months[monthIndex.get(monthKey(d))].completed++;
    });
    let cumC = createdBefore;
    let cumD = completedBefore;
    months.forEach(m => {
        cumC += m.created;
        cumD += m.completed;
        m.cumCreated = cumC;
        m.cumCompleted = cumD;
    });
    // Average over the last three FULL months (the current month is partial).
    const fullMonths = months.slice(0, -1).slice(-3);
    const avgCompletedPerMonth = fullMonths.length
        ? Math.round((fullMonths.reduce((s, m) => s + m.completed, 0) / fullMonths.length) * 10) / 10
        : 0;
    const remaining = total - counts.done;
    const monthsActive = months.filter(m => m.created > 0 || m.completed > 0).length;
    const throughput = {
        months,
        avgCompletedPerMonth,
        remaining,
        monthsToClear: avgCompletedPerMonth > 0 ? Math.ceil(remaining / avgCompletedPerMonth) : null,
        hasHistory: monthsActive >= 2,
        currentMonthPartial: true
    };

    // ── Overdue aging ───────────────────────────────────────────────────
    const aging = AGING_BUCKETS.map(b => ({ key: b.key, count: 0, taskIds: [] }));
    overdueTasks.forEach(task => {
        const days = daysBetween(isoDay(task.endDate), today);
        const idx = AGING_BUCKETS.findIndex(b => days >= b.min && days <= b.max);
        if (idx >= 0) {
            aging[idx].count++;
            aging[idx].taskIds.push(task.id);
        }
    });

    // ── Attention: overdue tasks grouped by repeated title ─────────────
    const groups = new Map();
    overdueTasks.forEach(task => {
        const key = groupKey(task);
        const days = daysBetween(isoDay(task.endDate), today);
        if (!groups.has(key)) {
            groups.set(key, {
                title: String(task.title || ''),
                projectId: task.projectId ?? null,
                projectName: projectName(task.projectId),
                count: 0,
                statusCounts: emptyCounts(),
                priorityCounts: Object.fromEntries(REPORT_PRIORITIES.map(priority => [priority, 0])),
                maxDaysOverdue: 0,
                highPriority: false,
                taskIds: []
            });
        }
        const g = groups.get(key);
        g.count++;
        g.statusCounts[normalizeStatus(task.status)]++;
        const priority = REPORT_PRIORITIES.includes(task.priority) ? task.priority : 'medium';
        g.priorityCounts[priority]++;
        g.maxDaysOverdue = Math.max(g.maxDaysOverdue, days);
        g.highPriority = g.highPriority || task.priority === 'high';
        g.taskIds.push(task.id);
    });
    const attentionAll = [...groups.values()].sort((a, b) =>
        (b.maxDaysOverdue - a.maxDaysOverdue) ||
        (Number(b.highPriority) - Number(a.highPriority)) ||
        a.title.localeCompare(b.title));

    // ── Upcoming window ─────────────────────────────────────────────────
    const windowEnd = addDays(today, upcomingDays);
    const upcoming = [];
    liveProjects.forEach(p => {
        const s = isoDay(p.startDate);
        const e = isoDay(p.endDate);
        const ptasks = tasksByProject.get(Number(p.id)) || [];
        if (s && s > today && s <= windowEnd) {
            upcoming.push({ kind: 'projectStart', date: s, name: String(p.name || ''), projectId: p.id, count: ptasks.length });
        }
        if (e && e >= today && e <= windowEnd) {
            upcoming.push({ kind: 'projectEnd', date: e, name: String(p.name || ''), projectId: p.id, count: ptasks.filter(t => normalizeStatus(t.status) !== 'done').length });
        }
    });
    const dueGroups = new Map();
    visibleTasks.forEach(task => {
        const e = isoDay(task.endDate);
        if (normalizeStatus(task.status) === 'done' || !e || e < today || e > windowEnd) return;
        const key = `${groupKey(task)}\u0000${e}`;
        if (!dueGroups.has(key)) {
            dueGroups.set(key, { kind: 'taskDue', date: e, name: String(task.title || ''), projectId: task.projectId ?? null, projectName: projectName(task.projectId), count: 0, taskIds: [] });
        }
        dueGroups.get(key).count++;
        dueGroups.get(key).taskIds.push(task.id);
    });
    upcoming.push(...dueGroups.values());
    const kindOrder = { projectStart: 0, projectEnd: 1, taskDue: 2 };
    upcoming.sort((a, b) => a.date.localeCompare(b.date) || kindOrder[a.kind] - kindOrder[b.kind] || a.name.localeCompare(b.name));

    // ── Data quality ────────────────────────────────────────────────────
    const dataQuality = {
        projectsWithoutTasks: projectRows.filter(r => r.total === 0).length,
        projectsWithoutDates: projectRows.filter(r => r.total > 0 && r.timePct === null && r.state !== 'notStarted').length,
        tasksWithoutProject: unassigned.length,
        tasksInArchivedProjects: inArchivedProjects.length,
        doneWithoutCompletedDate
    };

    return {
        cutDate: today,
        kpis: {
            totalTasks: total,
            counts,
            donePct: pct(counts.done, total),
            open,
            overdue: overdueTasks.length,
            overduePctOfOpen: pct(overdueTasks.length, open),
            projects: liveProjects.length,
            projectsInProgress,
            lateProjects: projectRows.filter(r => r.state === 'late').length,
            behindProjects: projectRows.filter(r => r.state === 'behind').length,
            createdThisMonth: visibleTasks.filter(t => { const c = createdDay(t); return c && c >= monthStart && c <= today; }).length,
            completedThisMonth: months[months.length - 1].completed,
            nextMilestone
        },
        projects: projectRows,
        unassigned: countRow(unassigned),
        archivedProjectTasks: countRow(inArchivedProjects),
        throughput,
        aging,
        attention: {
            rows: attentionAll.slice(0, attentionLimit),
            totalGroups: attentionAll.length,
            totalTasks: overdueTasks.length,
            // Tasks of the rows left out, so the report can link to exactly those.
            hiddenTaskIds: attentionAll.slice(attentionLimit).flatMap(g => g.taskIds)
        },
        upcoming: {
            days: upcomingDays,
            rows: upcoming.slice(0, upcomingLimit),
            total: upcoming.length,
            hiddenTaskIds: upcoming.slice(upcomingLimit).flatMap(u => u.taskIds || [])
        },
        dataQuality
    };
}
